import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Switch,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Alert,
  Skeleton,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import usePackagingCheckApi, { getApiErrorMessage } from "./usePackagingCheckApi";
import { BORDER, BRAND, TEXT_PRIMARY, TEXT_SECONDARY } from "./constants";

const KIND_LABELS = { section: "סעיף באריזה", detector: "זיהוי אוטומטי", text: "נוסח שחייב להופיע" };

const ChecklistRow = ({ item, onChange, onRemove }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "auto 1fr auto", sm: "auto 1fr 110px auto" },
      alignItems: "center",
      gap: 1.5,
      py: 1.25,
      "&:not(:last-of-type)": { borderBottom: `1px solid ${BORDER}` },
      opacity: item.enabled ? 1 : 0.55,
    }}
  >
    <Switch
      size="small"
      checked={item.enabled}
      onChange={(e) => onChange({ ...item, enabled: e.target.checked })}
      inputProps={{ "aria-label": `הפעלת ${item.label}` }}
    />
    <Box sx={{ minWidth: 0 }}>
      <TextField
        variant="standard"
        fullWidth
        value={item.label}
        onChange={(e) => onChange({ ...item, label: e.target.value })}
        InputProps={{ disableUnderline: true, sx: { fontSize: 14, fontWeight: 600, color: TEXT_PRIMARY } }}
        inputProps={{ "aria-label": "שם הפריט" }}
      />
      <Typography variant="caption" color={TEXT_SECONDARY}>
        {KIND_LABELS[item.kind]}
        {item.kind === "text" && item.target !== item.label ? `: "${item.target}"` : ""}
      </Typography>
    </Box>
    <Select
      size="small"
      value={item.severity}
      onChange={(e) => onChange({ ...item, severity: e.target.value })}
      sx={{ fontSize: 13, borderRadius: 2, display: { xs: "none", sm: "flex" } }}
      inputProps={{ "aria-label": "חומרה כשהפריט חסר" }}
    >
      <MenuItem value="critical">קריטי</MenuItem>
      <MenuItem value="medium">בינוני</MenuItem>
    </Select>
    {item.builtIn ? (
      <Box sx={{ width: 34 }} />
    ) : (
      <Tooltip title="הסרה">
        <IconButton size="small" onClick={onRemove} aria-label={`הסרת ${item.label}`}>
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

/** Settings of the mandatory items checklist (spec 3.5) */
const ChecklistSettingsDialog = ({ open, onClose }) => {
  const api = usePackagingCheckApi();
  const queryClient = useQueryClient();
  const [items, setItems] = useState([]);
  const [newPhrase, setNewPhrase] = useState("");

  const checklist = useQuery({ queryKey: ["packagingChecklist"], queryFn: api.getChecklist, enabled: open });
  useEffect(() => {
    if (open && checklist.data) setItems(checklist.data);
  }, [open, checklist.data]);

  const save = useMutation({
    mutationFn: api.saveChecklist,
    onSuccess: (saved) => {
      queryClient.setQueryData(["packagingChecklist"], saved);
      onClose();
    },
  });

  const addPhrase = () => {
    const phrase = newPhrase.trim();
    if (!phrase) return;
    setItems((prev) => [
      ...prev,
      { id: `text-${Date.now()}`, label: phrase, kind: "text", target: phrase, severity: "critical", enabled: true, builtIn: false },
    ]);
    setNewPhrase("");
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>צ'ק-ליסט פריטי חובה</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color={TEXT_SECONDARY} sx={{ mb: 2 }}>
          הפריטים שהמערכת מוודאת שמופיעים בכל גרסה מעודכנת. רק פריטים מופעלים נבדקים, ופריט חסר יופיע
          בבדיקות האוטומטיות בחומרה שנבחרה. השינויים חלים על בדיקות חדשות בלבד.
        </Typography>

        {checklist.isLoading && <Skeleton variant="rounded" height={240} />}
        {checklist.error && <Alert severity="error">{getApiErrorMessage(checklist.error, "לא ניתן לטעון את הצ'ק-ליסט")}</Alert>}

        {!checklist.isLoading && !checklist.error && (
          <>
            <Box sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, px: 1.5 }}>
              {items.map((item, index) => (
                <ChecklistRow
                  key={item.id}
                  item={item}
                  onChange={(updated) => setItems((prev) => prev.map((x, i) => (i === index ? updated : x)))}
                  onRemove={() => setItems((prev) => prev.filter((_, i) => i !== index))}
                />
              ))}
            </Box>

            <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
              <TextField
                size="small"
                fullWidth
                value={newPhrase}
                onChange={(e) => setNewPhrase(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPhrase())}
                placeholder='נוסח שחייב להופיע, לדוגמה: "להרחיק מהישג ידם של ילדים"'
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
              />
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={addPhrase}
                disabled={!newPhrase.trim()}
                sx={{ borderRadius: 2, whiteSpace: "nowrap", fontWeight: 600 }}
              >
                הוספה
              </Button>
            </Box>
          </>
        )}

        {save.error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {getApiErrorMessage(save.error, "שמירת הצ'ק-ליסט נכשלה")}
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: TEXT_SECONDARY, fontWeight: 600 }}>
          ביטול
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={() => save.mutate(items)}
          disabled={save.isPending || checklist.isLoading || Boolean(checklist.error)}
          sx={{ borderRadius: 2, fontWeight: 700, bgcolor: BRAND }}
        >
          שמירה
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChecklistSettingsDialog;
